/**
 * Shown in the chart pane in place of a chart the browser can't draw.
 *
 * A dimension with thousands of distinct values means thousands of DOM nodes, which is what
 * locks the tab. Not a data-volume problem — the rows are loaded either way.
 *
 * Offered rather than enforced: refusing outright would make a field unchartable just as
 * someone has a reason to look at it, so they get the count and the choice.
 */

import { Box, Button, Typography } from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';

interface ChartCardinalityNoticeProps {
    /** The dimension's display name, as the charts panel labels it. */
    fieldLabel: string;
    /** Distinct categories the chart would plot. */
    categoryCount: number;
    /** What this chart type can carry. */
    limit: number;
    /** Display name for the chart type, e.g. "Pie". Absent for types we can't name. */
    chartTypeLabel?: string;
    /** Draw it regardless. */
    onOverride: () => void;
}

const containerSx = {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    gap: 1,
    p: 3,
};

export default function ChartCardinalityNotice({
    fieldLabel,
    categoryCount,
    limit,
    chartTypeLabel,
    onOverride,
}: ChartCardinalityNoticeProps) {
    const chartName = chartTypeLabel ? `${chartTypeLabel.toLowerCase()} chart` : 'chart';

    return (
        <Box sx={containerSx}>
            <BarChartIcon fontSize='large' color='disabled' />

            <Typography variant='subtitle1'>Too many categories to chart</Typography>

            <Typography variant='body2' color='text.secondary'>
                <strong>{fieldLabel}</strong> has {categoryCount.toLocaleString()} distinct values —
                more than the {limit.toLocaleString()} a {chartName} can show.
            </Typography>

            <Typography variant='body2' color='text.secondary'>
                Filter the grid, or choose a field with fewer values.
            </Typography>

            <Button size='small' onClick={onOverride} sx={{ mt: 1 }}>
                Chart anyway
            </Button>

            <Typography variant='caption' color='text.secondary'>
                Drawing this many may freeze the page.
            </Typography>
        </Box>
    );
}
